import React, { useEffect, useRef, useState } from 'react';
import { View, Text, ScrollView, TouchableOpacity, Image, Keyboard } from 'react-native-web';
import * as Animatable from 'react-native-animatable';
import Feather from 'react-native-vector-icons/dist/Feather';
import { toast } from 'react-hot-toast';

// Child Components
import SavedPayment from './component_saved_payment';

// Components
import { Button, TextBox } from '../../components';

// Utils
import { storeData, toTitleCase, validateUSNumber } from '../../utils/util_helpers';
import { showAlert } from '../../utils/util_helpers';
import { getLogoPhoto } from '../../utils/util_truck_helpers';

// APIs
import { changePassword, clearAllUserData, getTaxList, getUserData, logOut, updateUserData } from '../../modules';

// Configs
import GLOBAL from '../../configs/config_global';
import { setGlobalState, STATE_KEYS, useGlobalState } from '../../configs/config_state';

// Navigation
import * as RootNavigation from '../../navigation/RootNavigation';
import routeKeys from '../../navigation/RouteKeys';
import { goSwitchAccount, updateAccountList, updateLocationList } from '../../utils/util_main_app';
import LoadingView from '../../components/component_loading_view';
import SubscriptionAgreementModal from './component_subscription_agreement';
import RouteKeys from '../../navigation/RouteKeys';
import { useRoute } from '@react-navigation/native';

export function AccountSettings() {
    const DELAY_VALUE = 150;
    const { goTo = '' } = useRoute().params || {};

    // region Refs
    const mainView = useRef(null);
    const txtOldPassword = useRef(null);
    const txtPassword = useRef(null);
    const txtContactName = useRef(null);
    const txtPhone = useRef(null);

    const savedPaymentModal = useRef(null);
    const subscriptionAgreementModal = useRef(null);
    const subscriptionTierModal = useRef(null);
    // endregion

    // region Global State
    const [globalAccountList] = useGlobalState(STATE_KEYS.ACCOUNT_LIST);
    const [selectedAccountIndex] = useGlobalState(STATE_KEYS.ACCOUNT_LIST_SELECTED_INDEX);
    const [currentScreenKey] = useGlobalState(STATE_KEYS.CURRENT_SCREEN_KEY);
    // endregion

    // region Local State
    const [selectedItemID, setSelectedItemID] = useState(1);
    const [contactName, setContactName] = useState(GLOBAL.DATA.USER.name);
    const [phone, setPhone] = useState(GLOBAL.DATA.USER.phone);
    const [oldPassword, setOldPassword] = useState('');
    const [password, setPassword] = useState('');
    const [showOldPassword, setShowOldPassword] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [isLoading, setLoading] = useState(false);
    const [isLoadingRemove, setLoadingRemove] = useState(false);
    // endregion

    // region Component Did Mount
    useEffect(() => {
        if (currentScreenKey.includes('AccountSettings-')) {
            setGlobalState(STATE_KEYS.ACCOUNT_SETTINGS_SCREEN_KEY, currentScreenKey);
        }
    }, [currentScreenKey]);
    // endregion

    // region Effect - Go To
    useEffect(() => {
        console.log('goTO', goTo)
        if (!!goTo?.trim()) {
            switch(goTo) {
                case 'subscription-settings':
                case 'payment-settings': {
                    setSelectedItemID(2);
                    mainView.current?.fadeInLeft(600);
                    break;
                }
                default: {
                    setSelectedItemID(1);
                }
            }
            RootNavigation.setParams({ goTo: '' });
        }
    }, [goTo]);
    // endregion

    // region Function - Update Account
    const executeUpdateAccount = () => {
        // Call API
        setLoading(true);
        updateUserData(contactName, phone)
            .then(() => showAlert({ text: 'Account information updated successfully' }))
            .catch((error) => showAlert({ text: 'Unable to update your profile, please try again later.\n\nError message: ' + error }))
            .finally(() => setLoading(false));
    }

    const goUpdateAccount = () => {
        Keyboard.dismiss();

        // Check for empty input
        if (!contactName?.trim() || !phone?.trim()) {
            showAlert({ text: 'Please fill in all fields before continuing' });
            return;
        }

        // Check valid US phone number
        if (!validateUSNumber(phone.replace('+1', ''))) {
            showAlert({ text: 'Your entered phone number is not a valid US number' });
            return;
        }

        // Confirmation before sending
        executeUpdateAccount();
    }
    // endregion

    // region Render Account Info Section
    const renderAccountInfoSection = () => (
        <ScrollView style={{ flex: 1, width: '100%' }} contentContainerStyle={{ padding: 48 }}>
            {/* Notice */}
            <View style={{
                width: '100%', backgroundColor: GLOBAL.COLOR.BASIC.DARK_BLUE, borderBottomWidth: 1, borderColor: GLOBAL.COLOR.DIVIDER,
                padding: 24, flexDirection: 'row', alignItems: 'center', borderRadius: 8, marginBottom: 16
            }}>
                <Image
                    source={require('../../images/iconLineInfo.png')}
                    style={{ width: 28, height: 28 }}
                    tintColor={'white'}
                />

                <Text style={{ fontSize: 18, color: 'white', marginLeft: 16, flex: 1, lineHeight: 30 }}>
                    We use this info for Truckster's vendor contact and support purposes only
                </Text>
            </View>

            {/* Email Input */}
            <TextBox
                key={'txtEmail'}
                title={'Email'}
                value={GLOBAL.DATA.USER.email}
                onPress={() => showAlert({ text: 'For security reasons, if you would like to update your login email address, please contact us at <b>vendor.support@gotruckster.com</b>' })}
            />

            {/* Contact Input */}
            <TextBox
                key={'txtContactName'}
                ref={txtContactName}
                title={'Contact Name'}
                placeholder={'Contact Name'}
                returnKeyType={'next'}
                autoCapitalize={'words'}
                onSubmitEditing={() => txtPhone.current?.focus()}
                onChangeText={setContactName}
                value={contactName}
            />

            {/* Phone Input */}
            <TextBox
                key={'txtPhone'}
                ref={txtPhone}
                title={'Phone'}
                placeholder={'Phone'}
                returnKeyType={'next'}
                autoCapitalize={'none'}
                keyboardType={'number-pad'}
                onSubmitEditing={() => Keyboard.dismiss()}
                onChangeText={setPhone}
                value={phone}
                useMaskedInput={true}
                showError={phone?.trim() ? !validateUSNumber(phone) : false}
            />

            {/* Save Button */}
            <Button
                style={{ marginTop: 16 }}
                text={'Save'}
                textStyle={{ color: 'white' }}
                locked={(contactName.trim() === GLOBAL.DATA.USER.name) && (phone.trim() === GLOBAL.DATA.USER.phone)}
                isLoading={isLoading}
                loadingText={'Saving account information'}
                onPress={goUpdateAccount}
            />
        </ScrollView>
    )
    // endregion

    // region Render Payment Section
    const renderPaymentSection = () => (
        <ScrollView style={{ flex: 1, width: '100%' }} contentContainerStyle={{ padding: 36, paddingTop: 20 }}>
            {/* Manage Subscription */}
            <Button
                style={{ marginTop: 16, backgroundColor: 'transparent', borderWidth: 2, borderColor: GLOBAL.COLOR.DIVIDER }}
                text={'Manage Your Subscription'}
                textStyle={{ color: GLOBAL.COLOR.BASIC.BLACK_TWO }}
                onPress={() => RootNavigation.navigate(RouteKeys.SUBSCRIPTION_TIERS)}
                image={'book-open'}
                imageColor={GLOBAL.COLOR.BASIC.BLACK_TWO}
                imageSize={28}
                imageNextToText={true}
                useFeather={true}
            />

            {/* Subscription Agreement */}
            <Button
                style={{ marginTop: 16, backgroundColor: 'transparent', borderWidth: 2, borderColor: GLOBAL.COLOR.DIVIDER }}
                text={'Subscription Agreement'}
                textStyle={{ color: GLOBAL.COLOR.BASIC.BLACK_TWO }}
                onPress={() => subscriptionAgreementModal.current?.showModal()}
                image={'pen-tool'}
                imageColor={GLOBAL.COLOR.BASIC.BLACK_TWO}
                imageSize={28}
                imageNextToText={true}
                useFeather={true}
            />

            {/* Saved Payment Method */}
            <Button
                style={{ marginTop: 16, backgroundColor: 'transparent', borderWidth: 2, borderColor: GLOBAL.COLOR.DIVIDER }}
                text={'Saved Payment Methods'}
                textStyle={{ color: GLOBAL.COLOR.BASIC.BLACK_TWO }}
                onPress={() => savedPaymentModal.current?.showModal()}
                image={'pocket'}
                imageColor={GLOBAL.COLOR.BASIC.BLACK_TWO}
                imageSize={28}
                imageNextToText={true}
                useFeather={true}
            />
        </ScrollView>
    )
    // endregion

    // region Render Password Section

    // region Function - Change Password
    const goChangePassword = () => {
        Keyboard.dismiss();

        // Check for empty input
        if (!oldPassword || !password) {
            showAlert({ text: 'Please fill in both fields before continuing' });
            return;
        }

        // Check same password used
        if (oldPassword?.trim() === password?.trim()) {
            showAlert({ text: 'Please choose a different new password' });
            return;
        }

        // Call API
        setLoading(true);
        changePassword(oldPassword, password)
            .then(() => {
                setOldPassword('');
                setPassword('');
                showAlert({ text: 'Password updated successfully, please note that you have been signed out from all other devices except this one for security purposes.' });
            })
            .catch((error) => showAlert({ text: 'Unable to update your password, please try again later.<br/><br/>Error message: ' + error }))
            .finally(() => setLoading(false));
    }
    // endregion

    const renderPasswordSection = () => (
        <ScrollView style={{ flex: 1, width: '100%' }} contentContainerStyle={{ padding: 48 }}>
            <Text style={{ fontWeight: 'bold', fontSize: 24, lineHeight: 30, color: GLOBAL.COLOR.BASIC.BLACK_TWO }}>
                Password Tips
            </Text>

            <Text style={{ fontSize: 18, lineHeight: 32, color: GLOBAL.COLOR.TEXT_GRAY, marginTop: 16, marginBottom: 24 }}>
                {
                    'For security, we recommend a strong password with (but not limited to) the following criteria:\n\n' +
                    '1. Make it at least 8 character long.\n' +
                    '2. Omit personal data (name, date of birth, etc.).\n' +
                    '3. Include both uppercase and lowercase letters.\n' +
                    '4. Include numbers.\n' +
                    '5. Include special characters (!, @, #, $, etc.).'
                }
            </Text>

            {/* Old Password Input */}
            <TextBox
                key={'txtOldPassword'}
                ref={txtOldPassword}
                containerStyle={{ marginTop: 8 }}
                title={'Current Password'}
                placeholder={'Current Password'}
                returnKeyType={'done'}
                autoCapitalize={'none'}
                secureTextEntry={!showOldPassword}
                showHintButton={true}
                hintButtonImage={'remove-red-eye'}
                hintButtonOpacity={showOldPassword ? 1 : 0.5}
                hintButtonColor={GLOBAL.COLOR.TEXT_GRAY}
                hintButtonOnPress={() => setShowOldPassword(!showOldPassword)}
                onChangeText={setOldPassword}
                value={oldPassword}
                onSubmitEditing={() => txtPassword.current?.focus()}
            />

            {/* Password Input */}
            <TextBox
                key={'txtPassword'}
                ref={txtPassword}
                containerStyle={{ marginTop: 8 }}
                title={'New Password'}
                placeholder={'New Password'}
                returnKeyType={'done'}
                autoCapitalize={'none'}
                secureTextEntry={!showPassword}
                showHintButton={true}
                hintButtonImage={'remove-red-eye'}
                hintButtonOpacity={showPassword ? 1 : 0.5}
                hintButtonColor={GLOBAL.COLOR.TEXT_GRAY}
                hintButtonOnPress={() => setShowPassword(!showPassword)}
                onChangeText={setPassword}
                value={password}
                showPasswordValidator={true}
                onSubmitEditing={() => Keyboard.dismiss()}
            />

            {/* Save Button */}
            <Button
                style={{ marginTop: 16 }}
                text={'Change Password'}
                textStyle={{ color: 'white' }}
                locked={!oldPassword?.trim() || !password?.trim()}
                isLoading={isLoading}
                loadingText={'Updating your account password...'}
                onPress={goChangePassword}
            />
        </ScrollView>
    )
    // endregion

    // region Render Sign Out Section
    const renderSignOutSection = () => (
        <View style={{ flex: 1, width: '100%', padding: 48 }}>
            <Text style={{ fontWeight: 'bold', fontSize: 24, lineHeight: 30, color: GLOBAL.COLOR.BASIC.BLACK_TWO }}>
                Signing Out
            </Text>

            <Text style={{ fontSize: 18, lineHeight: 32, color: GLOBAL.COLOR.TEXT_GRAY, marginTop: 16, marginBottom: 24 }}>
                Confirm that you would like to sign out of your account from this browser by clicking the button below
            </Text>

            {/* Save Button */}
            <Button
                style={{ marginTop: 16 }}
                text={'Sign Out'}
                textStyle={{ color: 'white' }}
                isLoading={isLoading}
                loadingText={'Signing you out...'}
                onPress={goLogout}
            />
        </View>
    )
    // endregion

    // region Render Switch Account Section

    // region Function - Remove Account from List
    const goRemoveAccount = (account) => {
        setLoadingRemove(true);
        logOut(account.id, account.token)
            .then((newAccountList) => {
                // Update main account list
                updateAccountList(newAccountList);

                // Notify user
                setLoadingRemove(false);
                showAlert({ text: 'Successfully removed account "' + account.data.name + '"' });
            });
    }
    // endregion

    // region Execute Account Switch
    const executeAccountSwitch = (item, index) => {
        setGlobalState(STATE_KEYS.LOADING_CONFIG, { loadingText: 'Switching account...' });
        getUserData(item.token, 'email')
            .then((userData) => {
                let accountData = {
                    token: item.token,
                    service: 'email',
                    data: userData
                };

                goSwitchAccount(accountData, index);
            })
            .catch((error) => {
                if (Number(error) === 401) {
                    showAlert({
                        text: `This account's session has been expired, please log in again.`,
                        cancelButton: {
                            text: 'OK',
                            onPress: () => goRemoveAccount(item)
                        }
                    });
                }
                else showAlert({ text: error });
            })
            .finally(() => setGlobalState(STATE_KEYS.LOADING_CONFIG, null))
    }
    // endregion

    // region Function - On Account Pressed
    const _onAccountPressed = (item, index, isSelected) => {
        if (isSelected) {
            showAlert({ text: 'This is your current active account' });
            return;
        }

        if (item.isExpired) {
            showAlert({
                text: `This account's session is expired, tap Confirm to remove it.<br><br/>You will need to re-login into this account later in order to use it.`,
                cancelButton: { text: 'Cancel' },
                confirmButton: {
                    text: 'Confirm',
                    color: 'red',
                    onPress: () => goRemoveAccount(item)
                }
            });
        }
        else {
            showAlert({
                text: 'Confirm switching your vendor app to this account?',
                cancelButton: { text: 'Cancel' },
                confirmButton: {
                    text: 'Confirm',
                    color: 'red',
                    onPress: () => executeAccountSwitch(item, index)
                }
            });
        }
    }
    // endregion

    // region Render Tag Item
    const renderTagItem = (text = '', textColor = GLOBAL.COLOR.BASIC.BLACK_TWO, backgroundColor = 'rgba(0, 0, 0 , 0.01)') => (
        <View style={{ paddingVertical: 6, paddingHorizontal: 12, backgroundColor: backgroundColor, borderRadius: 6, marginTop: 8, alignSelf: 'flex-start', marginRight: 8 }}>
            <Text style={{ color: textColor, fontSize: 12, fontWeight: 'bold' }}>
                {text}
            </Text>
        </View>
    )
    // endregion

    // region Render Account Item
    const renderAccountItem = (item, index) => {
        const isSelected = selectedAccountIndex === index;
        return(
            <TouchableOpacity
                key={`accountItem_${item.data.id}`}
                onPress={() => _onAccountPressed(item, index, isSelected)}
                style={{
                    alignSelf: 'stretch', padding: 24, flexDirection: 'row', alignItems: item.isExpired ? 'flex-start' : 'center',
                    backgroundColor: 'white', borderWidth: 2, borderColor: isSelected ? GLOBAL.COLOR.MAIN : GLOBAL.COLOR.DIVIDER, borderRadius: 8,
                    marginBottom: 24
                }}>
                {/* Truck Logo */}
                <View style={{ borderRadius: 32, width: 64, height: 64, overflow: 'hidden' }}>
                    {/* Image */}
                    <Image
                        source={getLogoPhoto(item.data['trucks'][0])}
                        style={{ width: 64, height: 64, backgroundColor: 'white' }}
                        resizeMode={'cover'}
                    />
                </View>

                {/* Truck Info */}
                <View style={{ alignItems: 'flex-start', marginLeft: 24, flex: 1 }}>
                    {/* Name */}
                    <Text style={{ color: GLOBAL.COLOR.BASIC.BLACK_TWO, fontWeight: '500', fontSize: 20, alignSelf: 'stretch' }} numberOfLines={2}>
                        {item.data['trucks'][0]['name']}
                    </Text>

                    {/* Email */}
                    <Text style={{ fontWeight: 'normal', color: GLOBAL.COLOR.TEXT_GRAY, fontSize: 18, marginTop: 8, alignSelf: 'stretch', opacity: isSelected ? 0.5 : 1 }} numberOfLines={1}>
                        {item.data['email']}
                    </Text>

                    {/* Expired Tag */}
                    {item.isExpired && renderTagItem('SESSION EXPIRED', GLOBAL.COLOR.MAIN, 'rgba(250, 57, 61, 0.2)')}
                </View>

                {/* Selected Indicator */}
                {
                    isSelected &&
                    <Image
                        source={require('../../images/iconLineStick.png')}
                        style={{ width: 32, height: 32, tintColor: GLOBAL.COLOR.MAIN }}
                        resizeMode={'contain'}
                    />
                }
            </TouchableOpacity>
        );
    }
    // endregion

    const renderSwitchAccountSection = () => (
        <ScrollView style={{ flex: 1, width: '100%' }} contentContainerStyle={{ padding: 48 }}>
            {/* Account List */}
            {globalAccountList.map((item, index) => renderAccountItem(item, index))}

            {/* Add Account Button */}
            <Button
                style={{ backgroundColor: GLOBAL.COLOR.BASIC.BLACK_TWO }}
                text={'Add Account'}
                textStyle={{ color: 'white' }}
                onPress={() => {
                    RootNavigation.push(routeKeys.LOGIN, { isAddingAccount: true });
                    setGlobalState(STATE_KEYS.ADDING_NEW_ACCOUNT, true);
                }}
            />
        </ScrollView>
    )
    // endregion

    // region Item List
    const itemList = [
        {
            id: 1,
            title: 'Account Information',
            icon: 'info',
            renderComp: () => renderAccountInfoSection()
        },
        {
            id: 2,
            title: 'Payment & Subscription',
            icon: 'credit-card',
            renderComp: () => renderPaymentSection()
        },
        {
            id: 3,
            title: 'Password & Security',
            icon: 'lock',
            renderComp: () => renderPasswordSection()
        },
        {
            id: 4,
            title: 'Switch Account',
            icon: 'refresh-cw',
            renderComp: () => renderSwitchAccountSection()
        },
        {
            id: 5,
            title: 'Sign Out',
            icon: 'log-out',
            renderComp: () => renderSignOutSection()
        }
    ];
    // endregion

    // region Render Item
    const renderItem = (itemData, index) => {
        const isChosen = selectedItemID === itemData.id;
        return(
            <Animatable.View
                key={`categoryItem_${itemData.id}`}
                animation={'fadeInUp'} duration={600} delay={index * DELAY_VALUE}
                style={{ width: '100%', borderBottomWidth: 1, borderColor: GLOBAL.COLOR.DIVIDER }}>
                <TouchableOpacity
                    onPress={() => {
                        if (!isChosen) {
                            setSelectedItemID(itemData.id);
                            mainView.current?.fadeInLeft(600);
                        }
                    }}
                    style={{
                        width: '100%', alignItems: 'center', flexDirection: 'row',
                        paddingHorizontal: 24, paddingVertical: 20,
                        backgroundColor: isChosen ? GLOBAL.COLOR.MAIN_SELECTED : 'white'
                    }}>
                    {/* Icon */}
                    <Feather name={itemData.icon || 'help-circle'} size={32} color={GLOBAL.COLOR.BASIC.BLACK_TWO} />

                    {/* Name */}
                    <Text style={{ color: GLOBAL.COLOR.BASIC.BLACK_TWO, fontSize: 18, lineHeight: 26, fontWeight: '500', textAlign: 'left', marginLeft: 16 }} numberOfLines={2}>
                        {toTitleCase(itemData.title)}
                    </Text>
                </TouchableOpacity>
            </Animatable.View>
        );
    }
    // endregion

    // region Function - Go Logout
    const goLogout = () => {
        showAlert({
            text: 'Are you sure you want to log out?',
            confirmButton: {
                text: 'Yes',
                color: GLOBAL.COLOR.BASIC.RED,
                onPress: () => {
                    const loggedOutAccountName = GLOBAL.DATA.USER.name;
                    setLoading(true);
                    setGlobalState(STATE_KEYS.LOADING_CONFIG, { loadingText: 'Logging out...' });
                    logOut()
                        .then((newAccountList) => {
                            // Switch to next available account on the list
                            if (newAccountList.length) {
                                const newAccount = newAccountList[0];
                                toast(`Logged out successfully from the account "${loggedOutAccountName}", you have been switched to "${newAccount.data.name}"`);

                                // Save selected account index to local storage
                                storeData('accountListIndex', JSON.stringify({ index: 0 }));

                                // Save vendor data to global state
                                GLOBAL.DATA.USER_TOKEN = newAccount.token;
                                GLOBAL.DATA.USER = newAccount.data;

                                // Update global location/account data
                                updateLocationList(0, newAccount.data.locations);
                                updateAccountList(newAccountList, 0);

                                // Get sales tax data
                                getTaxList(newAccount.token)
                                    .then((taxList) => {
                                        let taxObj = {};
                                        taxList.forEach((item) => taxObj[item.id] = item);
                                        GLOBAL.DATA.SALES_TAX_OPTIONS = taxObj;
                                    })
                                    .catch((error) => toast('Unable to fetch your sales tax options, please try again later in the Account Settings screen.\n\nError Message: ' + error));

                                // Move to home screen
                                setGlobalState(STATE_KEYS.CURRENT_TAB_STACK, routeKeys.PROFILE_STACK);
                                RootNavigation.reset(routeKeys.MAIN);
                            }
                            else {
                                toast('Logged out successfully from your account');

                                // Clear user data
                                clearAllUserData();

                                // Go back to welcome screen
                                RootNavigation.reset(routeKeys.WELCOME);
                            }
                        })
                        .finally(() => {
                            setLoading(false);
                            setGlobalState(STATE_KEYS.LOADING_CONFIG, null);
                        });
                }
            },
            cancelButton: { text: 'Cancel' }
        })
    }
    // endregion

    // region Render Modals
    const renderModals = () => (
        <>
            {/* Saved Payment Modal */}
            <SavedPayment ref={savedPaymentModal} />

            {/* Subscription Agreement Modal */}
            <SubscriptionAgreementModal ref={subscriptionAgreementModal} />
        </>
    )
    // endregion

    // region MAIN RENDER
    return(
        <View style={{ width: '100%', flex: 1 }}>
            {/* Nav Bar */}
            <View style={{
                flexDirection: 'row', width: '100%', alignItems: 'center',
                backgroundColor: 'white', borderBottomWidth: 1, borderBottomColor: GLOBAL.COLOR.DIVIDER,
                paddingVertical: 24, paddingHorizontal: 24
            }}>
                <Text style={{ fontSize: 28, fontWeight: '900' }}>
                    Account Settings
                </Text>
            </View>

            {/* Content */}
            <View style={{ flex: 1, width: '100%', flexDirection: 'row' }}>
                {/* Category */}
                <View style={{ width: '40%', minWidth: 350, maxWidth: 500, height: '100%', borderRightWidth: 1, borderColor: GLOBAL.COLOR.DIVIDER, zIndex: 100 }}>
                    <ScrollView
                        style={{ flex: 1, width: '100%', backgroundColor: 'white' }}
                        showsVerticalScrollIndicator={false}>
                        {itemList.map((item, index) => renderItem(item, index))}
                    </ScrollView>
                </View>

                {/* Items */}
                <Animatable.View
                    ref={mainView}
                    style={{ flex: 1, backgroundColor: 'white' }}>
                    {
                        selectedItemID ?
                            itemList[selectedItemID - 1]?.renderComp()
                            :
                            <View style={{ flex: 1, width: '100%', alignItems: 'center', justifyContent: 'center', padding: 64 }}>
                                <Text style={{ color: GLOBAL.COLOR.TEXT_LIGHT_GRAY, textAlign: 'center' }}>
                                    Select an item from the left to view its details
                                </Text>
                            </View>
                    }
                </Animatable.View>
            </View>

            {/* Modals */}
            {renderModals()}

            {/* Loading View */}
            {isLoadingRemove && <LoadingView description={'Removing account...'} />}
        </View>
    );
    // endregion
}
